import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LogOut } from "lucide-react";
import { logoutAction } from "../actions/auth";
import { RoutesPaths } from "../Router/constant";
import ConfirmDialog from "./ConfirmDialog";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  // Signing out mid-onboarding loses the steps that have not been saved yet,
  // so it is asked before it is done.
  const [confirming, setConfirming] = useState(false);
  const menuRef = useRef(null);
  const dispatch = useDispatch();
  // Populated by the verifyToken call dispatched once in AppShell.
  const user = useSelector((state) => state.auth.user);
  const displayName = user?.name || user?.email || "Guest";
  const initial = displayName.trim().charAt(0).toUpperCase() || "?";
  // Nothing to sign out of before the session is read; the menu would only
  // offer to log a guest out.
  const signedIn = Boolean(user?.id);

  // A dropdown that stays open after clicking away feels broken.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const askLogout = () => {
    setMenuOpen(false);
    setConfirming(true);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await dispatch(logoutAction());

    // A full load rather than a route change: this header sits on the sign-in
    // route, which is also the onboarding steps, and those hold their own
    // state about how far the user got. Dropping the page is the only way to
    // be sure the next person sees the sign-in form and nothing of the last
    // session.
    window.location.assign(RoutesPaths.LOGIN);
  };

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Link
          to="/"
          className="site-header__logo"
          onClick={() => setMenuOpen(false)}
        >
          <img src="/assets/logo.svg" alt="" className="img-fluid logoSize" />

          {/* <span>host</span>
          <span className="site-header__logo-o" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <circle cx="12" cy="12" r="11" fill="currentColor" />
              <circle cx="8.4" cy="10.2" r="1.4" fill="#fff" />
              <circle cx="15.6" cy="10.2" r="1.4" fill="#fff" />
              <path
                d="M8 14.5c1 1.4 2.6 2 4 2s3-.6 4-2"
                stroke="#fff"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span>r</span> */}
        </Link>

        <div className="site-header__actions" ref={menuRef}>
          <button type="button" className="site-header__help">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <circle cx="12" cy="12" r="9.25" />
              <path
                d="M9.4 9.3a2.6 2.6 0 1 1 3.9 2.25c-.75.45-1.3.9-1.3 1.85"
                strokeLinecap="round"
              />
              <circle
                cx="12"
                cy="16.7"
                r="0.15"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1.1"
              />
            </svg>
            <span className="site-header__help-label">Help</span>
          </button>

          <button
            type="button"
            className="site-header__user"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
          >
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="site-header__avatar site-header__avatar--img"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="site-header__avatar">{initial}</span>
            )}
            <span className="site-header__username">{displayName}</span>
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={`site-header__chevron${menuOpen ? " is-open" : ""}`}
            >
              <path
                d="M6 9l6 6 6-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {menuOpen && (
            <div className="site-header__menu" role="menu">
              <div className="site-header__menu-who">
                <strong>{user?.name || displayName}</strong>
                {user?.email && <span>{user.email}</span>}
              </div>

              <div className="site-header__menu-divider" />

              <button
                type="button"
                className="site-header__menu-item site-header__menu-item--danger"
                onClick={askLogout}
                disabled={loggingOut || !signedIn}
                role="menuitem"
              >
                {loggingOut ? (
                  <span className="install-status-spinner" aria-hidden="true" />
                ) : (
                  <LogOut size={16} strokeWidth={2} />
                )}
                {loggingOut ? "Signing out…" : "Log out"}
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title="Log out?"
        message="You will be signed out of Hostor and sent back to the sign-in page."
        confirmLabel={loggingOut ? "Signing out…" : "Yes, log out"}
        cancelLabel="Stay signed in"
        onConfirm={handleLogout}
        onCancel={() => !loggingOut && setConfirming(false)}
      />
    </header>
  );
}
